import { useEffect, useState, type CSSProperties } from 'react'
import { fetchSegmentView, type Period, type SegmentCategory } from '../landing'
import { ACTIVE_SUPPLIER } from '../tenant'
import { mainSegmentLabel, segmentMarketScope, segmentRowOnShareCard, type ShareCardFigure } from '../mainSegmentShare'
import { brand } from '../theme'
import { categoryLabel, formatQuantity, formatShare } from '../format'
import { InfoTip } from './InfoTip'

// Segment-aware standard view (Olof's KPI taxonomy, doc 5): per category (Snus /
// Vape / Smoking) the manufacturer's own sold volume + market share per leaf segment.
// Shows ONLY the categories/segments the supplier actually has — the backend omits
// the rest. Everything is counted in CU (Block 9) — the unit is labelled on each row.

// Backend unit ('dosor'/'units') → the label Olof uses: snus in CU (konsument-
// förpackningar), vape/smoking in units. Kept display-only — the backend string also
// keys the volume column, so it must not be renamed there.
// Block 9: every consumer-unit measure (snus dosor/cans, vape/smoking units) reads as CU — one
// term across the dashboard. Only a genuinely different measure (e.g. 'packages') passes through.
const unitLabel = (u: string): string => (u === 'dosor' || u === 'cans' || u === 'units' ? 'CU' : u)

// Backend leaf labels → clarified English (Olof: "all white snus, tobacco snus … och snus
// är rubriken"); the one translation table lives in format.ts (MP-OS-191).
const leafLabel = categoryLabel

// MP-OS-192: a row's share is hidden only when the Overview share card above shows the very same
// figure — same segment, same month, same basis (own CU ÷ the segment's permitted market CU).
// Showing it twice read as duplicated content (Olof, 22 Oct), so that row keeps its CU volume and
// points to the card. The card follows the supplier's main segment (MPD-68) and its period toggle,
// so the rule reads the card's actual state (`shareCard`) rather than assuming All White; with the
// card on all time, or on no figure, every row keeps its %. Admin whole-market rows show
// composition (% of own total), a different measure, so they keep it.
export function SegmentView({ period, shareCard = null }: { period: Period; shareCard?: ShareCardFigure | null }) {
  const [cats, setCats] = useState<SegmentCategory[] | null>(null)
  // The month the rows on screen are for, so a row is compared with the card only for its own month.
  const [catsMonth, setCatsMonth] = useState<string | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    setState('loading')
    fetchSegmentView(period.value)
      .then((c) => {
        if (cancelled) return
        setCats(c)
        setCatsMonth(period.value)
        setState(c.length ? 'ready' : 'empty')
      })
      .catch(() => !cancelled && setState('error'))
    return () => {
      cancelled = true
    }
  }, [period])

  if (state !== 'ready' || !cats) {
    return (
      <div className="catalog-empty">
        {state === 'loading'
          ? 'Loading…'
          : state === 'error'
            ? 'Could not load the segment view — try again in a moment.'
            : 'No segments for this supplier.'}
      </div>
    )
  }

  return (
    <div style={S.grid}>
      {cats.map((cat) => {
        const max = Math.max(1, ...cat.segments.map((s) => s.own))
        // Whole-market (admin) → own == market, so "market share" is 100 % everywhere (a
        // meaningless wall). Show segment COMPOSITION instead, with the bar matching the %
        // so fill and label agree. (WT-3 / WT-5)
        // MPD-76 F1: the API says so; a supplier that is its whole permitted market also has
        // own == market, so equal totals are only the fallback for an older API.
        const whole = cat.whole_market ?? (cat.market_total > 0 && cat.own_total === cat.market_total)
        const pctOf = (own: number) => (cat.own_total ? Math.round((own / cat.own_total) * 1000) / 10 : 0)
        const onCardRow = catsMonth ? segmentRowOnShareCard(shareCard, catsMonth, whole) : null
        const onCard = onCardRow != null && cat.segments.some((s) => s.segment === onCardRow) ? onCardRow : null
        return (
          <div style={S.card} key={cat.category}>
            <div style={S.head}>
              <span style={S.cat}>
                {categoryLabel(cat.category)}
                {/* MPD-64: what the split compares goes in the ⓘ, not under the table. */}
                <InfoTip label={`About the ${categoryLabel(cat.category).toLowerCase()} segments`} size="small">
                  {whole
                    ? 'Segment split — whole market, selected month.'
                    : `Own volume vs the market\u2019s in each segment — ${ACTIVE_SUPPLIER.label}, selected month.`
                      // MPD-76 F1: Vape/Smoking totals count only the supplier's product types (the share card's wording).
                      + segmentMarketScope(cat.category, ACTIVE_SUPPLIER.label).note}
                  {onCard && shareCard && ` The ${mainSegmentLabel(shareCard.segment)} share is on the share card above.`}
                </InfoTip>
              </span>
              <span style={S.tag}>{unitLabel(cat.unit)}</span>
            </div>
            <div style={S.totRow}>
              <span style={S.totVal}>{formatQuantity(cat.own_total)}</span>
              <span style={S.totUnit}>{unitLabel(cat.unit)} total</span>
              <span style={S.totShare}>
                {whole ? 'whole market' : `${formatShare(cat.share_total_pct, { hasSales: cat.own_total > 0 })} of ${categoryLabel(cat.category).toLowerCase()}`}
              </span>
            </div>
            {cat.segments.map((s) => {
              const pct = whole ? pctOf(s.own) : s.share_pct
              const barW = whole ? pct : (s.own / max) * 100
              const shareOnKpi = s.segment === onCard
              return (
                <div style={S.seg} key={s.segment}>
                  <span style={S.segName}>{leafLabel(s.segment)}</span>
                  <span style={S.track}>
                    <span style={{ ...S.fill, width: `${barW}%` }} />
                  </span>
                  <span style={S.segVal}>{formatQuantity(s.own)}</span>
                  {/* Empty cell (not removed) so the grid columns stay aligned with the other rows. */}
                  <span style={S.segShare} data-testid={`seg-share-${s.segment}`}>{shareOnKpi ? '' : formatShare(pct, { hasSales: s.own > 0 })}</span>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

const RB = 5
const S: Record<string, CSSProperties> = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 },
  card: {
    background: 'var(--surface, #fff)',
    border: '1px solid var(--line, #e1e1de)',
    borderRadius: 12,
    padding: '16px 18px',
  },
  head: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 },
  cat: { fontSize: 16, fontWeight: 700, color: 'var(--brand-dark)' },
  tag: {
    marginLeft: 'auto',
    fontSize: 11,
    color: brand.greyText,
    background: 'var(--brand-tint, #eef1f0)',
    padding: '2px 8px',
    borderRadius: 999,
  },
  totRow: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 },
  totVal: { fontSize: 24, fontWeight: 800, color: 'var(--ink, #111)', fontVariantNumeric: 'tabular-nums' },
  totUnit: { fontSize: 12, color: brand.greyText },
  totShare: { marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: brand.tealDark },
  seg: { display: 'grid', gridTemplateColumns: '96px 1fr auto auto', alignItems: 'center', gap: 8, marginBottom: 7 },
  segName: { fontSize: 12.5, color: 'var(--ink, #222)', fontWeight: 500 },
  track: { display: 'block', height: 10, background: 'var(--line, #ececec)', borderRadius: RB, overflow: 'hidden' },
  fill: { display: 'block', height: '100%', borderRadius: RB, background: 'var(--accent)' },
  segVal: { fontSize: 12, color: brand.greyText, fontVariantNumeric: 'tabular-nums', minWidth: 54, textAlign: 'right' },
  segShare: { fontSize: 12.5, fontWeight: 700, color: brand.tealDark, minWidth: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
}
